/**
 * Scroll-progress bar for a carousel.
 *
 * A thin track with a fill that grows as the carousel is scrolled across, so a
 * carousel showing 2 of 3 cards reads as scrollable without a peeking half-card.
 * The fill shows how much of the row has been SEEN, not how far the scroller has
 * travelled: it starts at the visible fraction rather than at zero, so it is never
 * empty and never full while cards remain off to the side.
 *
 * Usage — put it anywhere inside the element that also holds the scroller:
 *
 *   <div class="add-ons__carousel">
 *     …slideshow…
 *     <carousel-progress data-scroller="slideshow-slides">
 *       <span class="carousel-progress__fill"></span>
 *     </carousel-progress>
 *   </div>
 *
 * It hides itself when there is nothing to scroll, so the Liquid side doesn't have
 * to predict how many cards fit at the current width.
 *
 * Deliberately NOT a scroll-driven CSS animation: `animation-timeline` would need
 * `timeline-scope` plumbing through the slideshow's own DOM (which already uses
 * per-slide view timelines), and would silently do nothing on browsers without it.
 * This follows the same measure-and-set-width idiom as the mobile carousel progress
 * bar in sections/editorial-grid.liquid.
 */
class CarouselProgress extends HTMLElement {
  /** @type {HTMLElement | null} */
  #scroller = null;

  /** @type {AbortController | null} */
  #controller = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {number | null} */
  #frame = null;

  connectedCallback() {
    const selector = this.dataset.scroller || 'slideshow-slides';
    // The scroller is a sibling subtree, not a child — look from the parent.
    this.#scroller = /** @type {HTMLElement | null} */ (this.parentElement?.querySelector(selector) ?? null);
    if (!this.#scroller) return;

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#scroller.addEventListener('scroll', this.#schedule, { passive: true, signal });
    window.addEventListener('resize', this.#schedule, { signal });

    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(this.#schedule);
      this.#resizeObserver.observe(this.#scroller);
    }

    this.#schedule();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    if (this.#frame !== null) cancelAnimationFrame(this.#frame);
    this.#frame = null;
  }

  /** Coalesce scroll/resize bursts into one measurement per frame. */
  #schedule = () => {
    if (this.#frame !== null) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = null;
      this.#update();
    });
  };

  #update() {
    const scroller = this.#scroller;
    if (!scroller) return;

    const overflow = scroller.scrollWidth - scroller.clientWidth;
    // Sub-pixel overflow is a rounding artefact, not a scrollable row.
    const scrollable = overflow > 1;
    this.toggleAttribute('hidden', !scrollable);
    if (!scrollable) return;

    const seen = (Math.abs(scroller.scrollLeft) + scroller.clientWidth) / scroller.scrollWidth;
    const clamped = Math.min(1, Math.max(0, seen));
    this.style.setProperty('--carousel-progress', `${clamped * 100}%`);
  }
}

if (!customElements.get('carousel-progress')) {
  customElements.define('carousel-progress', CarouselProgress);
}
